import { cellToString } from "@/lib/mapper/cell";
import {
  isJsEngineReady,
  runUserJs,
  warmJsEngine,
} from "@/lib/mapper/js-sandbox";
import type { Profile, Rule } from "@/lib/mapper/types";
import type { TemplateValue } from "@/modules/_shared/templateValue";
import type { CellModule } from "@/modules/_shared/contract";

/** Convert a SourceRow into a plain JSON-safe object of string values. */
function plainRow(row: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(row ?? {}))
    out[k] = cellToString(row[k] as never);
  return out;
}

/** Extract out.Name / out["Name"] / out['Name'] references as workbook field
 *  segments, so dependency analysis sees them as `origin: "workbook"` deps. */
function extractOutRefs(code: string): TemplateValue[] {
  const refs = new Set<string>();
  collectOutRefs(code, 0, code.length, refs);
  return [...refs].map((r) => [{ t: "field", name: r, origin: "workbook" }]);
}

function collectOutRefs(
  code: string,
  start: number,
  end: number,
  refs: Set<string>,
) {
  let i = start;
  while (i < end) {
    const ch = code[i];
    if (ch === '"' || ch === "'") {
      i = skipQuoted(code, i, ch, end);
      continue;
    }
    if (ch === "`") {
      i = scanTemplateLiteral(code, i, end, refs);
      continue;
    }
    if (ch === "/" && code[i + 1] === "/") {
      i = skipLineComment(code, i, end);
      continue;
    }
    if (ch === "/" && code[i + 1] === "*") {
      i = skipBlockComment(code, i, end);
      continue;
    }
    if (startsIdentifier(code, i, "out")) {
      const next = readOutReference(code, i + 3, end);
      if (next) {
        refs.add(next.name);
        i = next.end;
        continue;
      }
    }
    i++;
  }
}

function startsIdentifier(code: string, index: number, word: string): boolean {
  if (code.slice(index, index + word.length) !== word) return false;
  const before = index > 0 ? code[index - 1] : "";
  const after = code[index + word.length] ?? "";
  return !isIdentifierChar(before) && !isIdentifierChar(after);
}

function readOutReference(
  code: string,
  index: number,
  end: number,
): { name: string; end: number } | null {
  let i = skipWhitespace(code, index, end);
  if (code[i] === ".") {
    i = skipWhitespace(code, i + 1, end);
    const ident = readIdentifier(code, i, end);
    return ident ? { name: ident.name, end: ident.end } : null;
  }
  if (code[i] === "[") {
    i = skipWhitespace(code, i + 1, end);
    const quote = code[i];
    if (quote !== '"' && quote !== "'") return null;
    const str = readQuotedValue(code, i, quote, end);
    if (!str) return null;
    const close = skipWhitespace(code, str.end, end);
    if (code[close] !== "]") return null;
    return { name: str.value, end: close + 1 };
  }
  return null;
}

function readIdentifier(
  code: string,
  index: number,
  end: number,
): { name: string; end: number } | null {
  if (!isIdentifierStart(code[index] ?? "")) return null;
  let i = index + 1;
  while (i < end && isIdentifierChar(code[i])) i++;
  return { name: code.slice(index, i), end: i };
}

function readQuotedValue(
  code: string,
  index: number,
  quote: string,
  end: number,
): { value: string; end: number } | null {
  let i = index + 1;
  let value = "";
  while (i < end) {
    const ch = code[i];
    if (ch === "\\") {
      if (i + 1 < end) value += code[i + 1];
      i += 2;
      continue;
    }
    if (ch === quote) return { value, end: i + 1 };
    value += ch;
    i++;
  }
  return null;
}

function scanTemplateLiteral(
  code: string,
  index: number,
  end: number,
  refs: Set<string>,
): number {
  let i = index + 1;
  while (i < end) {
    if (code[i] === "\\") {
      i += 2;
      continue;
    }
    if (code[i] === "`") return i + 1;
    if (code[i] === "$" && code[i + 1] === "{") {
      const close = findMatchingBrace(code, i + 2, end);
      if (close === -1) return end;
      collectOutRefs(code, i + 2, close, refs);
      i = close + 1;
      continue;
    }
    i++;
  }
  return end;
}

function findMatchingBrace(code: string, index: number, end: number): number {
  let depth = 1;
  let i = index;
  while (i < end) {
    const ch = code[i];
    if (ch === '"' || ch === "'") {
      i = skipQuoted(code, i, ch, end);
      continue;
    }
    if (ch === "`") {
      i = scanTemplateLiteral(code, i, end, new Set());
      continue;
    }
    if (ch === "/" && code[i + 1] === "/") {
      i = skipLineComment(code, i, end);
      continue;
    }
    if (ch === "/" && code[i + 1] === "*") {
      i = skipBlockComment(code, i, end);
      continue;
    }
    if (ch === "{") depth++;
    if (ch === "}") {
      depth--;
      if (depth === 0) return i;
    }
    i++;
  }
  return -1;
}

function skipQuoted(
  code: string,
  index: number,
  quote: string,
  end: number,
): number {
  let i = index + 1;
  while (i < end) {
    if (code[i] === "\\") {
      i += 2;
      continue;
    }
    if (code[i] === quote) return i + 1;
    i++;
  }
  return end;
}

function skipLineComment(code: string, index: number, end: number): number {
  let i = index + 2;
  while (i < end && code[i] !== "\n" && code[i] !== "\r") i++;
  return i;
}

function skipBlockComment(code: string, index: number, end: number): number {
  const close = code.indexOf("*/", index + 2);
  return close === -1 || close >= end ? end : close + 2;
}

function skipWhitespace(code: string, index: number, end: number): number {
  let i = index;
  while (i < end && /\s/.test(code[i])) i++;
  return i;
}

function isIdentifierStart(ch: string): boolean {
  return /[A-Za-z_$]/.test(ch);
}

function isIdentifierChar(ch: string): boolean {
  return /[\w$]/.test(ch);
}

export const javascriptModule: CellModule = {
  kind: "javascript",
  capability: "cell",
  name: "JavaScript",
  tooltip: "Transform the value with a little JavaScript",
  description:
    'Runs a small piece of JavaScript to build this column. Use row["Column"] for file columns and out["Column"] for already-computed columns, then return the value.',
  category: "Rules",
  tags: [
    "javascript",
    "js",
    "code",
    "script",
    "custom",
    "function",
    "transform",
  ],
  icon: { kind: "lucide", name: "Code2" },
  tone: "violet",
  defaultConfig: () => ({
    kind: "javascript",
    code: '// row["Column"] = file columns, out["Column"] = computed columns.\n// Return the value for this cell.\nreturn "";',
  }),
  resolve: (rule, ctx) => {
    if (rule.kind !== "javascript") return "";
    if (!isJsEngineReady()) {
      void warmJsEngine();
      return "";
    }
    return runUserJs(rule.code, {
      row: plainRow(ctx.row),
      out: ctx.output ?? {},
    });
  },
  templateStrings: (rule) =>
    rule.kind === "javascript" ? extractOutRefs(rule.code) : [],
};

/** All javascript rules in a profile, keyed by output column. Used by the
 *  preview/export orchestrators to warm the sandbox before the row loop. */
export function jsRulesInProfile(
  profile: Profile,
): { column: string; rule: Extract<Rule, { kind: "javascript" }> }[] {
  const out: { column: string; rule: Extract<Rule, { kind: "javascript" }> }[] =
    [];
  for (const [column, rule] of Object.entries(profile.fields ?? {})) {
    if (rule && (rule as Rule).kind === "javascript") {
      out.push({ column, rule: rule as Extract<Rule, { kind: "javascript" }> });
    }
  }
  return out;
}
